<template>
  <div :class="b(modifiers)">
    <span
      v-if="slug"
      :id="slug"
      :class="b('anchor')"
    ></span>

    <c-expansion-panel-toggle
      v-if="design === 'design2'"
      :title="title"
      :expanded="isExpanded"
      :background-color="toggleBackgroundColor"
      :icon-position="iconPosition"
      @toggle="toggleState"
    />
    <a
      v-else
      :class="b('toggle')"
      href="#"
      @click="toggleState"
    >
      <div :class="b('icon')"></div>
      <div :class="b('title')">
        {{ title }}
      </div>
    </a>

    <div
      v-if="isRendered"
      :class="b('content')"
      :style="{ maxHeight }"
    >
      <div
        ref="inner"
        :class="b('inner')"
      >
        <!-- @slot Used for item content -->
        <slot :is-open="isOpen"></slot>
      </div>
    </div>
  </div>
</template>

<script>
  import cExpansionPanelToggle from '@/components/c-expansion-panel-toggle.vue';
  import propScale from '@/helpers/prop.scale.js';
  import designMixin from '@/mixins/design.js';
  import url from '@/mixins/url.js';

  /**
   * Displays collapsible content panels. Use **c-collapse-group** as a wrapper for multiple items
   * and if only one item should be open at the same time (:one-active=true).
   */
  export default {
    name: 'c-collapse',

    components: {
      cExpansionPanelToggle,
    },

    mixins: [url, designMixin],

    props: {
      /**
       * Sets state of the item.
       */
      active: {
        type: Boolean,
        default: false,
      },

      /**
       * Defines the padding of the inner content. Only takes effect when designsystem is not set.
       */
      padding: propScale(500, [0, 500]),

      /**
       * Adjusts the visual size of the collapsable.
       */
      size: propScale(500, [300, 500]),

      /**
       * Title of the toggle item.
       */
      title: {
        type: String,
        required: true,
      },

      /**
       * Allows to define the separator style/position.
       */
      separator: {
        type: String,
        default: 'default',
        validator(value) {
          return ['default', 'below'].includes(value);
        },
      },

      /**
       * Enables scroll position update, after the collapse opened, to make sure the toggle and content are visible.
       */
      keepInView: {
        type: Boolean,
        default: false,
      },

      /**
       * The slug of the tab element. Used for anchor links
       */
      slug: {
        type: String,
        default: '',
      },

      /**
       * String.
       */
      iconPosition: {
        type: String,
        default: 'right',
      },

      /**
       * String.
       */
      toggleBackgroundColor: {
        type: String,
        default: 'lightgray',
      },
    },

    emits: ['afterOpen', 'c-collapse.opened', 'c-collapse.toggled'],

    data() {
      return {
        /**
         * @type {Boolean} Defines the current state of the collapse.
         */
        isExpanded: this.active,

        /**
         * @type {String|Number} The maxHeight of the content used for transition.
         */
        maxHeight: '',

        /**
         * @type {Number} The timeout for transition open the collapse.
         */
        openTimeout: null,

        /**
         * @type {Number} The timeout for the transition before it can start closing the collapse.
         */
        closeTimeout: null,

        /**
         * @type {Boolean} Defines if the collapse is currently open. This also waits until the transition is finished,
         * so that calculations depending on the height of the content can be done by the child component.
         */
        isOpen: this.active,

        /**
         * @type {Boolean} Flag shows if the collapse content is already rendered in the DOM.
         */
        isRendered: this.active,

        /**
         * @type {Object|null} May holds a collapse or collapse group component, which is also parent of this component.
         */
        relevantParentComponent: null,
      };
    },

    computed: {
      /**
       * Sets modifier classes depending on props.
       *
       * @returns   {Object}  modifiers   Modifier BEM classes
       */
      modifiers() {
        return {
          expanded: this.isExpanded,
          open: this.isOpen,
          // NOTE: padding property only takes effect when design system is not set.
          padding: this.design === 'design2' ? null : this.padding,
          separator: this.separator,
          size: this.size,
          design: this.design,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    created() {
      this.relevantParentComponent = this.findRelevantCollapseParent();
    },
    // beforeMount() {},
    mounted() {
      this.$mitt.on('c-collapse-group.toggle', (payload) => {
        const toggledElement = payload.toggledCollapse;
        const toggleGroup = payload.component;

        // close all items except toggled if 'one-active=true'
        if (this.isExpanded && toggleGroup === this.relevantParentComponent && this !== toggledElement) {
          this.isExpanded = false;

          this.close();
        }
      });

      this.handleHashChange();
      window.addEventListener('hashchange', this.handleHashChange);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('hashchange', this.handleHashChange);
    },
    // unmounted() {},

    methods: {
      /**
       * Open the collapse depending on the url hash.
       */
      handleHashChange() {
        const hash = this.getHashValue();

        if (hash && this.slug && !this.isExpanded && hash === this.slug) {
          this.$nextTick(() => {
            this.isExpanded = true;
            this.open();
            this.emitToggled();
          });
        }
      },

      /**
       * Find the closest parent component of the collapse which is a collapse or collapse group itself.
       *
       * @param {Object} component - A Vue component instance.
       *
       * @returns {Object|null}
       */
      findRelevantCollapseParent(component = this) {
        if (!component?.$parent) {
          return null;
        }

        if (['c-collapse-group', 'c-collapse'].includes(component.$parent.$options.name)) {
          return component.$parent;
        }

        return this.findRelevantCollapseParent(component.$parent);
      },

      /**
       * Toggles state and emits event to EventBus.
       *
       * @param   {Object}    event   Original event
       */
      toggleState(event) {
        event.preventDefault();

        event.target.blur();

        this.isExpanded = !this.isExpanded; // toggle state

        if (this.isExpanded) {
          this.open();
        } else {
          this.close();
        }

        this.emitToggled();
      },

      /**
       * Opens the collapsible.
       */
      open() {
        if (this.isRendered) {
          this.openContent();
        } else {
          this.isRendered = true;

          this.$nextTick(() => {
            this.openContent();
          });
        }
      },

      /**
       * Sets the max-height of the content and opens the content.
       */
      openContent() {
        this.setMaxHeight();
        clearTimeout(this.closeTimeout);

        this.openTimeout = setTimeout(() => {
          this.maxHeight = 'none';
          this.isOpen = true;

          if (this.keepInView) {
            this.scrollIntoView();
          }

          /**
           * Emits opened event to EventBus.
           *
           * @event c-collapse.opened
           * @property {Object} payload - The events payload.
           * @property {Object} payload.component - Current component
           */
          this.$mitt.emit('c-collapse.opened', { component: this });

          /**
           * @event afterOpen - Is called, after the collapsable opened.
           *
           * @type {Object} The related component instance.
           */
          this.$emit('afterOpen', this);
        }, 400);
      },

      /*
       * Emits toggled event to EventBus
       */
      emitToggled() {
        /**
         * Emits toggled event to EventBus.
         *
         * @event c-collapse.toggled
         * @property {Object} payload - The events payload.
         * @property {Object} payload.component - Current component.
         * @property {Object} payload.relevantParent - The components relevant parent component.
         */
        this.$mitt.emit('c-collapse.toggled', {
          component: this,
          relevantParent: this.relevantParentComponent,
        });
      },

      /**
       * Keeps the top of the collapse inside the viewport, if content too high.
       */
      scrollIntoView() {
        const rect = this.$el.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (rect.top < 0 || (rect.top > viewportHeight / 4 && rect.bottom > viewportHeight)) {
          this.$el.scrollIntoView({ behavior: 'smooth' });
        }
      },

      /**
       * Closes the collapsible.
       */
      close() {
        this.setMaxHeight();
        this.isOpen = false;

        clearTimeout(this.openTimeout);

        this.closeTimeout = setTimeout(() => {
          this.maxHeight = 0;
        }, 20); // Timeout is needed because of transition
      },

      /**
       * Sets the collapsible height according to its current content.
       */
      setMaxHeight() {
        this.maxHeight = `${this.$refs.inner.clientHeight}px`;
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  // @use '@/setup/scss/extends' as *;

  c-collapse {
    display: block;
    padding: functions.rem((variables.$spacing--25 + variables.$spacing--60) 0);

    @include mixins.media(md) {
      padding: functions.rem((variables.$spacing--25 + variables.$spacing--80) variables.$spacing--90);
    }

    &:not([active]) {
      padding-right: 0;
      padding-bottom: 0;
      padding-left: 0;

      * {
        display: none;
      }
    }

    &.c-collapse--padding-0 {
      padding: 0;
    }
  }

  .c-collapse {
    $this: &;

    &--design-design2 {
      #{$this}__inner {
        padding: functions.rem(30px);
      }
    }

    &__toggle {
      @include mixins.font(variables.$font-size--18, variables.$font-size--24);

      position: relative;
      display: flex;
      align-items: center;
      height: functions.rem(60px);
      border-bottom: 0;
      cursor: pointer;
      color: functions.get-theme-var('c-collapse-toggle-color');
      text-decoration: none !important; // stylelint-disable-line declaration-no-important

      @include mixins.media(md) {
        height: functions.rem(80px);
        padding-right: functions.rem(variables.$spacing--40);
        padding-left: functions.rem(variables.$spacing--40);
      }

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }

      &::after {
        position: absolute;
        bottom: 0;
        left: 0;
        content: '';
        width: 100%;
        border-bottom: 1px solid functions.get-theme-var('c-collapse-separator-color');
        transition: border-color variables.$transition-duration--400 ease-in-out;
      }

      &:hover {
        color: functions.get-theme-var('c-collapse-toggle-color--hover');
        text-decoration: none;
      }
    }

    &__icon {
      position: relative;
      flex: 0 0 auto;
      width: functions.rem(16px);
      height: functions.rem(16px);
      margin-right: functions.rem(variables.$spacing--20);

      @include mixins.media(md) {
        width: functions.rem(25px);
        height: functions.rem(25px);
        margin-right: functions.rem(variables.$spacing--25);
      }

      &::before,
      &::after {
        position: absolute;
        content: '';
        background-color: functions.get-theme-var('c-collapse-toggle-icon-color');
      }

      &::before {
        top: 50%;
        width: 100%;
        height: 1px;
      }

      &::after {
        top: 50%;
        width: 100%;
        height: 1px;
        transform: rotate(-90deg);
        transition: transform variables.$transition-duration--400 ease-in-out;
      }
    }

    &__anchor {
      position: absolute;
      top: calc(var(--header-height) * -1);
      right: 0;
      left: 0;
      visibility: hidden;
    }

    &__title {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &__content {
      @include mixins.font(variables.$font-size--16);

      max-height: 0;
      overflow: hidden;
      opacity: 0;
      transition:
        max-height variables.$transition-duration--400 ease-in-out,
        opacity variables.$transition-duration--400 ease-in-out;
    }

    &__inner {
      #{$this}--padding-0 & {
        padding: 0;
      }

      #{$this}--padding-500 & {
        padding: functions.rem(variables.$spacing--25 0);

        @include mixins.media(md) {
          padding: functions.rem(variables.$spacing--25 variables.$spacing--90);
        }
      }
    }

    &--open {
      > #{$this}__content {
        max-height: none;
        overflow: visible;
        opacity: 1;
      }
    }

    &--expanded {
      > #{$this}__toggle {
        &::after {
          border-color: functions.get-theme-var('c-collapse-separator-color--expanded');
        }

        > #{$this}__icon::after {
          transform: rotate(0deg);
        }
      }
    }

    &--separator-below {
      border-bottom: 1px solid functions.get-theme-var('c-collapse-separator-color');

      #{$this}__inner {
        padding-top: 0;
      }

      #{$this}__toggle::after {
        display: none;
      }
    }

    &--size-300 {
      #{$this}__toggle {
        height: functions.rem(50px);

        @include mixins.media(md) {
          height: functions.rem(65px);
        }
      }

      #{$this}__icon {
        width: functions.rem(14px);
        height: functions.rem(14px);
        margin-right: functions.rem(variables.$spacing--15);

        @include mixins.media(md) {
          width: functions.rem(20px);
          height: functions.rem(20px);
          margin-right: functions.rem(variables.$spacing--20);
        }
      }
    }

    // NOTE: Neutralize implicit spacing in some component.
    .c-slider-1 {
      padding-top: 0;
      padding-bottom: 0;
    }

    .c-cms-wysiwyg,
    .c-cms-image {
      &:first-child {
        padding-top: 0;
      }

      &:last-child {
        padding-bottom: 0;
      }
    }
  }
</style>
